import { Dispatch, SetStateAction, useEffect } from 'react';

import { Alert, Button, Col, Container, Form, Modal, Row } from 'react-bootstrap';
import { GMetrixLoader } from 'components/GMetrixLoader/GMetrixLoader';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { GMetrixDialog } from 'components/GMetrixDialog/GMetrixDialog';
import { GMetrixInput } from 'components/GMetrixInput/GMetrixInput';
import { type UserPermissions } from 'pages/users/types';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNotification } from 'app/notifications';
import { PartnerPermissions } from 'pages/partners';
import { useUsers } from 'pages/users/context';
import { PartnerTypes } from 'apis/PartnerApi';
import { useNavigate } from 'react-router-dom';
import { User, UsersApi } from 'apis/UsersApi';
import { useSuperState } from 'hooks';
import { useApp } from 'app';

import 'pages/users/components/Details/styles/details.scss';

type DetailsProps = {
    getAllUsers: (partnerId: number) => void;
    handleUserUpdate: (userDetails: User) => void;
    partnerId?: number;
    permissions: UserPermissions | PartnerPermissions;
    setUserDetails: Dispatch<SetStateAction<User>>;
    setUserDetailsOriginal: Dispatch<SetStateAction<User>>;
    userDetails: User;
    userDetailsOriginal: User;
}

export const Details = (props: DetailsProps) => {

    const { getAllUsers, handleUserUpdate, permissions, setUserDetails, setUserDetailsOriginal, userDetails, userDetailsOriginal } = props;

    const [state, setState] = useSuperState({
        canResend: false,
        error: null as string,
        loading: false,
        loadingCustomTestsForModal: {
            data: [],
            loading: true
        },
        resendingInvite: false,
        showConfirmModal: false,
        showEditUserModal: false

    });
    const { canResend, error, loading, loadingCustomTestsForModal, resendingInvite, showConfirmModal, showEditUserModal } = state;

    const { highestRole, selectedPartner, userId } = useApp();
    const { formatMessage } = useIntl();
    const { currentPartnerId } = useUsers();
    const { catchWithToast, createToast, onSuccessToast } = useNotification();
    const navigate = useNavigate();

    // Partner types whose admins are subject to the impersonation hierarchy rules.
    const restrictedImpersonationTypes = [PartnerTypes.SuperAdmin, PartnerTypes.Distributor, PartnerTypes.Reseller, PartnerTypes.TestingCenter];
    const isRestrictedPartnerType = restrictedImpersonationTypes.includes(selectedPartner?.type);

    const ownPartnerId = highestRole?.partnerId ?? 0;
    const targetPartnerId = userDetails?.CurrentPartnerId ?? 0;

    // Two users are siblings when they belong to the same partner. Siblings may
    // never impersonate one another, regardless of partner type.
    const isSiblingUser = ownPartnerId > 0 && targetPartnerId === ownPartnerId;

    // For the restricted partner types, also hide the button while managing your
    // own partner context.
    const isManagingOwnPartner = ownPartnerId > 0 && selectedPartner?.id === ownPartnerId;

    const showImpersonateButton = !isSiblingUser && (!isRestrictedPartnerType || !isManagingOwnPartner);
    const clearError = () => setState({ error: null });

    const resendInvite = async () => {

        try {

            setState({ resendingInvite: true });
            const result = await UsersApi.ResendWelcomeEmail(userDetails.Id);

            if (result?.Done) {

                onSuccessToast(result.Message || formatMessage({ id: 'welcome_email_resent.b71c2ee1', defaultMessage: 'Welcome email resent.' }));

            } else {

                createToast(result?.Message || formatMessage({ id: 'unable_to_resend_the_welcome_email.63ce3902', defaultMessage: 'Unable to resend the welcome email.' }), 'warning');

            }

        } catch (e) {

            catchWithToast(e, formatMessage({ id: 'failed_to_resend_the_welcome_email.62258af5', defaultMessage: 'Failed to resend the welcome email.' }));

        } finally {

            setState({ resendingInvite: false });

        }

    };

    useEffect(() => {

        const loadResendEligibility = async () => {

            try {

                const eligible = await UsersApi.CanResendWelcomeEmail(userDetails.Id);
                setState({ canResend: eligible });

            } catch {

                setState({ canResend: false });

            }

        };

        if (userDetails?.Id) {

            loadResendEligibility();

        }

    }, [userDetails?.Id]);

    const editFullName = (value: string, firstOrLast: 'firstName' | 'lastName') => {

        const splitFullName = userDetails?.FullName?.split(' ');
        let newFullNameAsString;
        if (firstOrLast === 'firstName') {

            newFullNameAsString = `${value} ${splitFullName && splitFullName[splitFullName.length - 1]}`;

        } else {

            newFullNameAsString = `${splitFullName && splitFullName[0]} ${value}`;

        }
        return newFullNameAsString;

    };

    const impersonateUser = (userId: string) => {

        UsersApi.ImpersonateUser(userId);

    };

    const handleConfirmDeleteModal = async () => {

        try {

            const customTests = await UsersApi.GetUsersCustomTests(userDetails.UserProfileID);
            setTimeout(() => {

                setState({
                    loadingCustomTestsForModal: { data: customTests, loading: false }
                });

            }, 1000);

        } catch {

            // Failed to load custom tests

        }
        setState({ showConfirmModal: true });

    };

    const handleDeleteUserModal = async () => {

        const response = await UsersApi.RemoveUserFromTC(userDetails.UserProfileID, currentPartnerId !== 256 ? currentPartnerId : -1);
        if (response.Done) {

            createToast(formatMessage({ id: 'succesfully_removed_user_from_testing_ce.ce25d53d', defaultMessage: 'Succesfully removed user from Testing Center' }), 'success');
            setTimeout(() => {

                navigate('/users');

            }, 2000);

        } else {

            createToast(formatMessage({ id: 'failed_to_remove_user_from_testing_cente.b3368bfd', defaultMessage: 'Failed to remove user from Testing Center' }) + ' ' + response.Message, 'danger');

        }
        getAllUsers(currentPartnerId !== 256 ? currentPartnerId : -1);

    };

    const handleSubmit = async (e: any) => {

        e.preventDefault();

        if (!userDetails) return;

        try {

            setState({ loading: true });

            await UsersApi.Update(userDetails);
            handleUserUpdate(userDetails);

            setState({ loading: false });

            setUserDetailsOriginal(userDetails);
            setState({ showEditUserModal: false });

        } catch (e){

            setState({
                error: e?.message || e,
                loading: false
            });

        }

    };

    const setActiveStatus = (value: string) => {

        const isActiveBool = value === 'true';
        const updatedPartnerList = [...userDetails.PartnerList];
        updatedPartnerList[0].Active = isActiveBool;
        setUserDetails({ ...userDetails, PartnerList: updatedPartnerList });

    };

    return (
        <>
            {!userDetails ?
                (
                    <GMetrixLoader />
                )
                :
                (
                    <div>

                        <div>
                            <div className="d-flex justify-content-between">
                                <h3 className="text-strong">
                                    <FormattedMessage defaultMessage="General Info" id="general_info.d56ce80b" />
                                </h3>

                                {permissions.Edit && permissions.UserEdit && (
                                    <Button onClick={() => setState({ showEditUserModal: true })}>
                                        <i className="fa-solid fa-pen me-2"></i>
                                        <FormattedMessage defaultMessage="Edit" id="edit.00978daf" />
                                    </Button>
                                )}
                            </div>

                            <div className="p-4">

                                <div>
                                    <p className="text-strong fw-semibold mb-2">
                                        <FormattedMessage defaultMessage="Username" id="username.dceab582" />
                                    </p>
                                    <p>
                                        {userDetails.UserName}
                                    </p>
                                </div>

                                <div>
                                    <p className="text-strong fw-semibold mb-2">
                                        <FormattedMessage defaultMessage="First Name" id="first_name.759a675b" />
                                    </p>
                                    <p>
                                        {userDetailsOriginal.FirstName}
                                    </p>
                                </div>

                                <div>
                                    <p className="text-strong fw-semibold mb-2">
                                        <FormattedMessage defaultMessage="Last Name" id="last_name.029e7273" />
                                    </p>
                                    <p>
                                        {userDetailsOriginal.LastName}
                                    </p>
                                </div>

                                <div>
                                    <p className="text-strong fw-semibold mb-2">
                                        <FormattedMessage defaultMessage="Status" id="status.ad856ad7" />
                                    </p>
                                    <p>
                                        {userDetailsOriginal.PartnerList[0].Active ? <FormattedMessage defaultMessage="Active" id="active.b9616c88" /> : <FormattedMessage defaultMessage="Inactive" description={formatMessage({ id: 'new.03d9347f', defaultMessage: 'New' })} id="inactive.5fb6d21a" />}
                                    </p>
                                </div>

                                <div>
                                    <p className="text-strong fw-semibold mb-2">
                                        <FormattedMessage defaultMessage="Early Access" id="early_access.86082dfe" />
                                    </p>
                                    <p>
                                        {userDetailsOriginal.EarlyAccessPreference === 0 ? (
                                            <FormattedMessage defaultMessage="All - Default to Early Access features, show opt-out option." id="all_default_to_early_access_features_sho.28e0b508" />
                                        ) : userDetailsOriginal.EarlyAccessPreference === 1 ? (
                                            <FormattedMessage defaultMessage="Opt-in - Default to Standard features, show opt-in option." id="opt_in_default_to_standard_features_show.76482b7c" />
                                        ) : (
                                            <FormattedMessage defaultMessage="None - Default to Standard features, don't show opt-in option." id="none_default_to_standard_features_dont_s.1a693924" />
                                        )}
                                    </p>
                                </div>

                            </div>
                        </div>

                        {userId !== userDetails.UserProfileID && (
                            <Row>

                                <Col className="my-3" xl={12}>
                                    <div className="select-roles__role d-flex align-items-center">
                                        <FormattedMessage defaultMessage="Actions" id="actions.61fd1d29" />
                                    </div>
                                </Col>

                                <Col className="my-2" xl={12}>
                                    {showImpersonateButton && (
                                        <GMetrixButton
                                            className="m-2"
                                            disabled={!permissions.Authenticate || !permissions.Edit || !permissions.UserEdit} onClick={() => impersonateUser(userDetails.Id)}
                                            variant="primary"
                                        >
                                            <FormattedMessage defaultMessage="Impersonate User" id="impersonate_user.54d49e91" />
                                        </GMetrixButton>
                                    )}
                                    <GMetrixButton
                                        className="m-2"
                                        disabled={resendingInvite || !canResend || !permissions.Edit || !permissions.UserEdit}
                                        onClick={resendInvite}
                                        variant="primary"
                                    >
                                        <FormattedMessage defaultMessage="Resend Invite Email" id="resend_invite_email.56fa9cb5" />
                                    </GMetrixButton>
                                    <GMetrixButton
                                        className="m-2"
                                        disabled={!permissions.Edit || !permissions.UserEdit}
                                        onClick={handleConfirmDeleteModal}
                                        variant="danger"
                                    >
                                        <FormattedMessage defaultMessage="Remove User" id="remove_user.4584c8fe" />
                                    </GMetrixButton>
                                </Col>

                            </Row>
                        )}

                        <Modal onHide={() => setState({ showEditUserModal: false })} show={showEditUserModal}>

                            <Modal.Header closeButton>
                                <Modal.Title>
                                    <FormattedMessage defaultMessage="Edit Account" id="edit_account.3a8dbb59" />
                                </Modal.Title>
                            </Modal.Header>

                            <Form onSubmit={handleSubmit}>

                                <Modal.Body>
                                    <Alert dismissible onClose={clearError} show={!!error} variant={'danger'}>
                                        {error}
                                    </Alert>

                                    <div className="mb-3">
                                        <p className="text-strong fw-semibold mb-2">
                                            <FormattedMessage defaultMessage="Username/Email" id="usernameemail.9e110d3a" />
                                        </p>
                                        <p>
                                            {userDetails.UserName}
                                        </p>
                                    </div>

                                    <div className="mb-3 w-50">
                                        <GMetrixInput
                                            required
                                            onChange={e => setUserDetails({
                                                ...userDetails,
                                                FirstName: e.target.value,
                                                FullName: editFullName(e.target.value, 'firstName')
                                            })}
                                            defaultValue={userDetailsOriginal.FirstName}
                                            disabled={loading}
                                            label={formatMessage({ id: 'first_name.759a675b', defaultMessage: 'First Name' })}
                                            readOnly={!permissions.Edit || !permissions.UserEdit}
                                            size="sm"
                                        />
                                    </div>

                                    <div className="mb-3 w-50">
                                        <GMetrixInput
                                            required
                                            onChange={e => setUserDetails({
                                                ...userDetails,
                                                FullName: editFullName(e.target.value, 'lastName'),
                                                LastName: e.target.value
                                            })}
                                            defaultValue={userDetailsOriginal.LastName}
                                            disabled={loading}
                                            label={formatMessage({ id: 'last_name.029e7273', defaultMessage: 'Last Name' })}
                                            readOnly={!permissions.Edit || !permissions.UserEdit}
                                            size="sm"
                                        />
                                    </div>

                                    {userId !== userDetails.UserProfileID && (
                                        <div className="mb-3 w-50">
                                            <Form.Label className="fw-bold">
                                                <FormattedMessage defaultMessage="Status" id="status.ad856ad7" />
                                            </Form.Label>
                                            <Form.Select
                                                defaultValue={userDetailsOriginal.PartnerList[0].Active.toString()}
                                                disabled={loading}
                                                name={formatMessage({ id: 'status.ad856ad7', defaultMessage: 'Status' })}
                                                onChange={e => setActiveStatus(e.target.value)}
                                                size="sm"
                                            >
                                                <option
                                                    key={1}
                                                    title={formatMessage({ id: 'active.b9616c88', defaultMessage: 'Active' })}
                                                    value="true"
                                                >
                                                    {formatMessage({ id: 'active.b9616c88', defaultMessage: 'Active' })}
                                                </option>
                                                <option
                                                    key={2}
                                                    title={formatMessage({ id: 'inactive.5ee46a1c', defaultMessage: 'Inactive', description: 'New' })}
                                                    value="false"
                                                >
                                                    {formatMessage({ id: 'inactive.5ee46a1c', defaultMessage: 'Inactive', description: 'New' })}
                                                </option>
                                            </Form.Select>
                                        </div>
                                    )}

                                    <div>
                                        <Form.Label className="fw-bold">
                                            <FormattedMessage defaultMessage="Early Access" id="early_access.86082dfe" />
                                        </Form.Label>
                                        <Form.Check
                                            required
                                            checked={userDetails.EarlyAccessPreference === 2}
                                            disabled={loading}
                                            id="earlyAccessNone"
                                            label={formatMessage({ id: 'none_default_to_standard_features_dont_s.1a693924', defaultMessage: 'None - Default to Standard features, don\'t show opt-in option.' })}
                                            name="Early Access"
                                            onChange={() => setUserDetails({ ...userDetails, EarlyAccessPreference: 2 })}
                                            type="radio"
                                        />
                                        <Form.Check
                                            required
                                            checked={userDetails.EarlyAccessPreference === 1}
                                            disabled={loading}
                                            id="earlyAccessOptIn"
                                            label={formatMessage({ id: 'opt_in_default_to_standard_features_show.76482b7c', defaultMessage: 'Opt-in - Default to Standard features, show opt-in option.' })}
                                            name="Early Access"
                                            onChange={() => setUserDetails({ ...userDetails, EarlyAccessPreference: 1 })}
                                            type="radio"
                                        />
                                        <Form.Check
                                            required
                                            checked={userDetails.EarlyAccessPreference === 0}
                                            disabled={loading}
                                            id="earlyAccessAll"
                                            label={formatMessage({ id: 'all_default_to_early_access_features_sho.28e0b508', defaultMessage: 'All - Default to Early Access features, show opt-out option.' })}
                                            name="Early Access"
                                            onChange={() => setUserDetails({ ...userDetails, EarlyAccessPreference: 0 })}
                                            type="radio"
                                        />
                                    </div>

                                </Modal.Body>

                                <Modal.Footer>
                                    <Button className="me-2" disabled={loading} onClick={() => setState({ showEditUserModal: false })} variant="secondary">
                                        <FormattedMessage defaultMessage="Cancel" id="cancel.89a86adc" />
                                    </Button>
                                    <Button disabled={loading} type="submit" variant="primary">
                                        <FormattedMessage defaultMessage="Save" id="save.d44082ae" />
                                    </Button>
                                </Modal.Footer>
                            </Form>

                        </Modal>

                        <GMetrixDialog
                            actionButtonClick={() => {

                                setState({
                                    loadingCustomTestsForModal: { data: [], loading: true },
                                    showConfirmModal: false
                                });

                                handleDeleteUserModal();

                            }}
                            onCancel={() => {

                                setState({
                                    loadingCustomTestsForModal: { data: [], loading: true },
                                    showConfirmModal: false
                                });

                            }}
                            actionButtonDisabled={false}
                            actionButtonText={formatMessage({ id: 'yes.104886cd', defaultMessage: 'Yes', description: 'Formulario' })}
                            closeButtonText={formatMessage({ id: 'no.4dd074da', defaultMessage: 'No', description: 'Formulario' })}
                            dialogLabel={formatMessage({ id: 'delete_user_from_testing_center.872f1723', defaultMessage: 'Delete User From Testing Center' })}
                            show={showConfirmModal}
                        >
                            <Container>

                                <Row>
                                    <Col className="kendo-dialog-text-only">
                                        <FormattedMessage defaultMessage="Deleting this user from your testing center will also remove all of their custom tests. Would you like to process?" id="deleting_this_user_from_your_testing_cen.fa120f29" />
                                    </Col>
                                </Row>

                                <Row className="mb-3">
                                    <Col className="col-12 mb-3">
                                        <h2>
                                            <FormattedMessage defaultMessage="Custom Tests" id="custom_tests.9e7494ea" />
                                            {' '}
                                            {!loadingCustomTestsForModal.loading && `(${loadingCustomTestsForModal.data.length})`}
                                        </h2>
                                    </Col>

                                    {!loadingCustomTestsForModal?.loading ?
                                        (
                                            <Col className="details__custom_test_container">
                                                {loadingCustomTestsForModal.data.length > 0 ?
                                                    (
                                                        <>
                                                            {loadingCustomTestsForModal.data.map((customTestName, i: number) => (
                                                                <Col className="col-12 details__custom_test_item" key={`${i}_${customTestName}`}>
                                                                    {customTestName}
                                                                </Col>
                                                            ))}
                                                        </>
                                                    )
                                                    :
                                                    (
                                                        <Col className="col-12 details__custom_test_item d-flex justify-content-center">
                                                            <FormattedMessage defaultMessage="No Custom Test Records" id="no_custom_test_records.1a723aa5" />
                                                        </Col>
                                                    )
                                                }
                                            </Col>
                                        )
                                        :
                                        (
                                            <Col className="col-12 d-flex justify-content-center my-2 details__custom_test_item">
                                                <GMetrixLoader />
                                            </Col>
                                        )
                                    }
                                </Row>
                            </Container>
                        </GMetrixDialog>
                    </div>
                )
            }
        </>
    );

};
